import React, { useEffect, useState } from 'react';

import { SidePanel } from '@flipdish/ui-library/components/organisms/SidePanel';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Translate } from 'react-localize-redux';

import flippyLauncher from '../../assets/images/flippy-launcher.png';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import FlipdishAgentMFE from './FlipdishAgentMFE';

/** Dispatch `new Event(OPEN_EVENT)` on `window` to open the assistant from anywhere in the portal. */
export const OPEN_EVENT = 'flipdish-agent-open';

export const useFlipdishAssistantEnabled = () => {
  const flagOn = useFeatureFlagValue(PortalFeatureFlags.flipdishAssistant, false);
  const localDev =
    process.env.NODE_ENV === 'development' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  return flagOn || localDev;
};

/** Top-nav Flippy button — opens the assistant drawer. */
export const FlipdishAgentHeaderButton = () => {
  if (!useFlipdishAssistantEnabled()) return null;

  return (
    <IconButton
      onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}
      aria-label="Open Flipdish Assistant"
      data-fd="flipdish-agent-open"
      size="large"
      sx={{
        ml: 0.5,
        p: 0.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'common.white',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <img src={flippyLauncher} alt="" width={32} height={32} style={{ display: 'block' }} />
    </IconButton>
  );
};

/**
 * The assistant as `SidePanel` content for the new application header shell. The panel supplies the
 * close button; the MFE owns its own composer, so there is no `SidePanel.Footer`.
 */
export const FlipdishAgentPanelContent = () => (
  <>
    <SidePanel.Header>
      <h2 className="pr-14 text-lg font-semibold text-primary">
        <Translate id="Flipdish_Assistant" />
      </h2>
    </SidePanel.Header>
    <SidePanel.Content className="gap-0 px-0 lg:px-0">
      <ErrorBoundary identifier="flipdish-agent-widget" includeTowlie={false}>
        <FlipdishAgentMFE />
      </ErrorBoundary>
    </SidePanel.Content>
  </>
);

/** Right drawer hosting the agent MFE. Mount once in Portal (not a route). Legacy top bar only. */
export const FlipdishAgentAssistant = () => {
  const enabled = useFlipdishAssistantEnabled();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const onOpen = () => {
      setMounted(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      keepMounted
      disableEnforceFocus
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: 16,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <Typography variant="h6" style={{ flex: 1, fontSize: 18, fontWeight: 600 }}>
            <Translate id="Flipdish_Assistant" />
          </Typography>
          <IconButton
            aria-label="Close Flipdish Assistant"
            onClick={() => setOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {mounted && (
            <ErrorBoundary identifier="flipdish-agent-widget" includeTowlie={false}>
              <FlipdishAgentMFE />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default FlipdishAgentAssistant;
